import { useState, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const TIME_RANGES = [
  { label: '1W', days: 7 },
  { label: '1M', days: 30 },
  { label: '3M', days: 90 },
  { label: '6M', days: 180 },
  { label: '1Y', days: 365 },
  { label: 'All', days: 9999 },
] as const;

export const PerformanceChart = () => {
  const { user } = useAuth();
  const [range, setRange] = useState(30);

  const { data: snapshots, isLoading } = useQuery({
    queryKey: ['portfolio-snapshots', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('portfolio_snapshots')
        .select('total_equity, snapshot_at')
        .eq('user_id', user.id)
        .order('snapshot_at', { ascending: true })
        .limit(1000);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
    staleTime: 60_000,
  });

  const chartData = useMemo(() => {
    if (!snapshots || snapshots.length < 2) return [];
    const cutoff = range < 9000 ? new Date(Date.now() - range * 86400000) : new Date(0);
    return snapshots
      .filter(s => s.snapshot_at && new Date(s.snapshot_at) >= cutoff)
      .map(s => ({
        date: new Date(s.snapshot_at!).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        value: Number(s.total_equity),
      }));
  }, [snapshots, range]);

  const startValue = chartData.length > 0 ? chartData[0].value : 0;
  const isPositive = chartData.length > 0 ? chartData[chartData.length - 1].value >= startValue : true;
  const strokeColor = isPositive ? 'hsl(var(--accent-green))' : 'hsl(var(--accent-red))';

  if (isLoading) return <Card className="border-border/30"><CardContent className="p-4"><Skeleton className="h-[200px]" /></CardContent></Card>;

  return (
    <Card className="border-border/30">
      <CardHeader className="pb-1 px-4 pt-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-heading flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4 text-primary" /> Performance
          </CardTitle>
          <div className="flex gap-0.5">
            {TIME_RANGES.map(r => (
              <Button
                key={r.label}
                variant={range === r.days ? 'default' : 'ghost'}
                size="sm"
                className="h-6 px-2 text-[10px]"
                onClick={() => setRange(r.days)}
              >
                {r.label}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {chartData.length < 2 ? (
          <div className="flex flex-col items-center justify-center h-[180px] text-muted-foreground">
            <TrendingUp className="w-8 h-8 mb-2 opacity-40" />
            <p className="text-xs">Collecting performance data...</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="perfGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={strokeColor} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={strokeColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v / 1000).toFixed(1)}k`} width={48} />
              <Tooltip
                contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}
                formatter={(v: number) => [`$${v.toLocaleString()}`, 'Value']}
              />
              <Area type="monotone" dataKey="value" stroke={strokeColor} strokeWidth={2} fill="url(#perfGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};
